import { useQuery } from '@tanstack/react-query'
import { Globe, Mail, ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { errorMessage, toApiError } from '@/api/errors'
import { ErrorState, LoadingState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { FormField } from '@/components/forms/FormField'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { PageHeader } from '@/components/ui/PageHeader'
import { settingsQuery, useSaveSettings } from '@/features/platform/api'
import type { SiteSettings } from '@/features/platform/types'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

export function PlatformSettingsPage() {
  const { t } = useTranslation('platform')
  useDocumentTitle('Settings')
  const settings = useQuery(settingsQuery())
  return (
    <>
      <PageHeader
        title={t('Settings')}
        description={t('Addresses of the website, the console and the schools.')}
        breadcrumbs={[{ label: t('Settings') }]}
        homeHref="/platform"
      />
      {settings.isPending ? (
        <LoadingState />
      ) : settings.error ? (
        <ErrorState description={errorMessage(settings.error)} onRetry={() => void settings.refetch()} />
      ) : (
        <SettingsForm key={settings.data.baseDomain ?? ''} settings={settings.data} />
      )}
    </>
  )
}

function SettingsForm({ settings }: { settings: SiteSettings }) {
  const { t } = useTranslation('platform')
  const save = useSaveSettings()
  const [baseDomain, setBaseDomain] = useState(settings.baseDomain ?? '')
  const [contactEmail, setContactEmail] = useState(settings.contactEmail ?? '')
  const [contactPhone, setContactPhone] = useState(settings.contactPhone ?? '')
  const [errors, setErrors] = useState<{
    baseDomain?: string
    contactEmail?: string
    contactPhone?: string
    form?: string
  }>({})
  const domain = baseDomain.trim().toLowerCase() || 'example.com'
  const changingDomain = !!settings.baseDomain && baseDomain.trim().toLowerCase() !== settings.baseDomain

  const submit = () => {
    setErrors({})
    save.mutate(
      {
        baseDomain: baseDomain.trim() || null,
        contactEmail: contactEmail.trim() || null,
        contactPhone: contactPhone.trim() || null,
      },
      {
        onSuccess: (s) => {
          toast.success(t('Settings saved'))
          // The console itself moves when the domain changes.
          if (changingDomain && s.platformUrl && !window.location.href.startsWith(s.platformUrl)) {
            window.location.href = `${s.platformUrl}/platform/settings`
          }
        },
        onError: (e) => {
          const apiError = toApiError(e)
          const next: typeof errors = {}
          for (const v of apiError.violations)
            if (v.field === 'baseDomain' || v.field === 'contactEmail' || v.field === 'contactPhone')
              next[v.field] = v.message
          setErrors(Object.keys(next).length ? next : { form: apiError.message })
        },
      },
    )
  }

  return (
    <div className="grid items-start gap-6 lg:grid-cols-3">
      <Card className="lg:col-span-2">
        <CardHeader
          icon={<Globe />}
          title={t('Domain')}
          description={t('All sites live under one domain name.')}
        />
        <CardBody className="space-y-5">
          {errors.form ? (
            <Alert tone="danger" title={t('The settings were not saved')}>
              {errors.form}
            </Alert>
          ) : null}
          <FormField
            label={t('Base domain')}
            error={errors.baseDomain}
            hint={t('e.g. example.com — without https:// or www.')}
          >
            <Input
              value={baseDomain}
              onChange={(e) => setBaseDomain(e.target.value)}
              placeholder="example.com"
              spellCheck={false}
            />
          </FormField>
          <dl className="grid gap-3 rounded-control border border-line bg-surface-muted/40 p-4 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-xs text-muted">{t('Website')}</dt>
              <dd className="mt-0.5 font-medium break-all text-ink">{`www.${domain}`}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted">{t('Console')}</dt>
              <dd className="mt-0.5 font-medium break-all text-ink">{`platform.${domain}`}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted">{t('A school')}</dt>
              <dd className="mt-0.5 font-medium break-all text-ink">{`${t('school')}.${domain}`}</dd>
            </div>
          </dl>
          {changingDomain ? (
            <Alert tone="warning" title={t('Before changing the domain')}>
              {t(
                'Point {{domain}} and *.{{domain}} to this server in your DNS. Certificates for every address are then issued automatically within a few minutes; the old addresses stop working.',
                { domain },
              )}
            </Alert>
          ) : null}
          <FormField
            label={t('Contact email')}
            error={errors.contactEmail}
            hint={t('Shown on the website for demo requests.')}
          >
            <Input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
          </FormField>
          <FormField
            label={t('Contact phone')}
            error={errors.contactPhone}
            hint={t('Shown on the website next to the email, e.g. +261 38 73 532 76.')}
          >
            <Input type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
          </FormField>
          <div className="flex justify-end">
            <Button onClick={submit} loading={save.isPending} leftIcon={<Mail />}>
              {t('Save')}
            </Button>
          </div>
        </CardBody>
      </Card>
      <Card>
        <CardHeader icon={<ShieldCheck />} title={t('HTTPS certificates')} />
        <CardBody className="space-y-3 text-sm text-ink-soft">
          <p>
            {t(
              'The server issues and renews a free certificate (Let’s Encrypt) for every address: the website, the console and each school.',
            )}
          </p>
          <p>
            {t('A new school or a changed address is secured within a few minutes, with nothing to do here.')}
          </p>
          <p className="text-xs text-muted">
            {t('Reserved addresses: {{list}}.', { list: settings.reserved.join(', ') })}
          </p>
        </CardBody>
      </Card>
    </div>
  )
}
